import React, { useState, useEffect } from 'react';
import { ArrowLeft, Shield, Cpu, Brain, Zap, CheckCircle, Play, Loader, Terminal, FileText, X, Printer } from 'lucide-react';
import { Incident } from './Dashboard';
import api from '../api';
import ReportModal from './ReportModal';
import Framework5D from './Framework5D';
import RemediationProgress, { RemediationStep } from './RemediationProgress';

interface IncidentDetailProps {
  onRemediated?: (id: string) => void;
  incident: Incident;
  onBack: () => void;
  autoRemediate?: boolean;
}

// Get remediation steps based on incident source
const getRemediationSteps = (incident: Incident): RemediationStep[] => {
  if (incident.source === 'aws') {
    if (incident.type.includes('EC2') || incident.type.includes('Crypto') || incident.type.includes('Trojan')) {
      return [
        { id: 'analyze', label: 'Analyzing threat context', status: 'pending' },
        { id: 'snapshot', label: 'Creating forensic snapshot', status: 'pending' },
        { id: 'isolate', label: 'Isolating EC2 instance', status: 'pending', detail: 'Modifying security groups...' },
        { id: 'revoke', label: 'Revoking IAM credentials', status: 'pending' },
        { id: 'verify', label: 'Verifying isolation', status: 'pending' },
      ];
    }
    if (incident.type.includes('S3') || incident.type.includes('Bucket')) {
      return [
        { id: 'analyze', label: 'Analyzing bucket exposure', status: 'pending' },
        { id: 'block', label: 'Blocking public access', status: 'pending', detail: 'Applying bucket policy...' },
        { id: 'audit', label: 'Auditing bucket contents', status: 'pending' },
        { id: 'verify', label: 'Verifying access blocked', status: 'pending' },
      ];
    }
    if (incident.type.includes('IAM') || incident.type.includes('Credential')) {
      return [
        { id: 'analyze', label: 'Analyzing credential usage', status: 'pending' },
        { id: 'disable', label: 'Disabling access key', status: 'pending', detail: 'Deactivating compromised key...' },
        { id: 'rotate', label: 'Initiating key rotation', status: 'pending' },
        { id: 'notify', label: 'Notifying security team', status: 'pending' },
      ];
    }
  }
  
  if (incident.source === 'okta') {
    return [
      { id: 'analyze', label: 'Analyzing session activity', status: 'pending' },
      { id: 'revoke', label: 'Revoking active sessions', status: 'pending', detail: 'Terminating user sessions...' },
      { id: 'mfa', label: 'Enforcing MFA re-authentication', status: 'pending' },
      { id: 'suspend', label: 'Suspending user account', status: 'pending' },
      { id: 'notify', label: 'Sending security alert', status: 'pending' },
    ];
  }
  
  if (incident.source === 'crowdstrike') {
    return [
      { id: 'analyze', label: 'Analyzing endpoint behavior', status: 'pending' },
      { id: 'contain', label: 'Containing host', status: 'pending', detail: 'Network isolation in progress...' },
      { id: 'scan', label: 'Running deep scan', status: 'pending' },
      { id: 'quarantine', label: 'Quarantining malicious files', status: 'pending' },
      { id: 'verify', label: 'Verifying containment', status: 'pending' },
    ];
  }

  return [
    { id: 'analyze', label: 'Analyzing threat', status: 'pending' },
    { id: 'remediate', label: 'Executing remediation', status: 'pending' },
    { id: 'verify', label: 'Verifying resolution', status: 'pending' },
  ];
};

const IncidentDetail: React.FC<IncidentDetailProps> = ({ incident, onBack, autoRemediate = false, onRemediated }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [executing, setExecuting] = useState(false);
  const [executed, setExecuted] = useState(incident.status === 'remediated');
  const [executionResult, setExecutionResult] = useState<string | null>(null);
  const [activityLog, setActivityLog] = useState<{time: string, message: string, type: 'info' | 'success' | 'warning'}[]>([]);
  const [showReport, setShowReport] = useState(false);
  const [remediationSteps, setRemediationSteps] = useState<RemediationStep[]>(() => getRemediationSteps(incident));

  useEffect(() => {
    if (autoRemediate && !analyzing && !analysis && !executed) {
      runAnalysis();
    }
  }, [autoRemediate]);

  useEffect(() => {
    if (autoRemediate && analysis && !executing && !executed) {
      executeRemediation();
    }
  }, [autoRemediate, analysis]);

  const addLog = (message: string, type: 'info' | 'success' | 'warning' = 'info') => {
    setActivityLog(prev => [...prev, { time: new Date().toLocaleTimeString(), message, type }]);
  };

  const updateStepStatus = (stepId: string, status: RemediationStep['status'], detail?: string) => {
    setRemediationSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, status, detail: detail || step.detail } : step
    ));
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    setCurrentStep(1);
    setActivityLog([]);
    addLog('Starting threat analysis pipeline...', 'info');

    try {
      addLog('Ingesting threat data from ' + incident.source.toUpperCase() + '...', 'info');
      await new Promise(r => setTimeout(r, 800));
      setCurrentStep(2);

      addLog('Running ML anomaly detection...', 'info');
      await new Promise(r => setTimeout(r, 600));
      setCurrentStep(3);

      addLog('Classifying threat with DNN model...', 'info');
      await new Promise(r => setTimeout(r, 600));
      setCurrentStep(4);

      addLog('Consulting Arbitium AI for remediation strategy...', 'info');
      
      const result = await api.ai.analyze({
        id: incident.id,
        source: incident.source,
        type: incident.type,
        title: incident.title,
        severity: incident.severity,
        description: incident.description,
      }, false);

      setAnalysis(result);
      setCurrentStep(5);
      
      addLog('ML Anomaly Score: ' + (result.pipeline.ml.anomalyScore * 100).toFixed(1) + '%', 'success');
      addLog('DNN Classification: ' + result.pipeline.dnn.classification + ' (' + result.pipeline.dnn.confidence + '% confidence)', 'success');
      addLog('MITRE ATT&CK: ' + result.pipeline.dnn.mitreTactic + ' - ' + result.pipeline.dnn.mitreId, 'success');
      addLog('AI Recommendation: ' + (result.pipeline.genai.action || 'Execute remediation'), 'success');
      addLog('Analysis complete in ' + result.pipeline.totalTimeMs + 'ms', 'success');
    } catch (error: any) {
      addLog('Error: ' + error.message, 'warning');
    }
    
    setAnalyzing(false);
  };

  const executeRemediation = async () => {
    if (!analysis) return;
    
    setExecuting(true);
    addLog('Executing remediation actions...', 'info');

    // Animate through remediation steps
    const steps = remediationSteps;
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      updateStepStatus(step.id, 'in-progress');
      addLog(step.label + '...', 'info');
      
      // Simulate step execution time (300-800ms per step)
      await new Promise(r => setTimeout(r, 300 + Math.random() * 500));
      
      updateStepStatus(step.id, 'complete');
      addLog('✓ ' + step.label + ' complete', 'success');
    }

    try {
      const result = await api.ai.remediate(
        { id: incident.id, source: incident.source, type: incident.type, title: incident.title, severity: incident.severity, description: incident.description },
        analysis.pipeline?.genai
      );

      if (result.success || result.actions?.length > 0) {
        result.actions?.forEach((action: string) => addLog(action, 'success'));
        setExecutionResult(result.actions?.join('; ') || 'Remediation complete');
        setExecuted(true);
        if (onRemediated) onRemediated(incident.id);
        setCurrentStep(6);
        addLog('✓ All remediation steps complete!', 'success');
        
        if ((window as any).markThreatRemediated) {
          (window as any).markThreatRemediated(incident.id);
        }
      } else {
        result.errors?.forEach((error: string) => addLog(error, 'warning'));
      }
    } catch (error: any) {
      addLog('Remediation error: ' + error.message, 'warning');
      setExecutionResult('Error: ' + error.message);
    }

    setExecuting(false);
  };

  const getRemediationCommands = () => {
    if (analysis?.pipeline?.genai?.commands?.length > 0) {
      return analysis.pipeline.genai.commands;
    }
    if (incident.source === 'aws') {
      if (incident.type.includes('EC2') || incident.type.includes('Crypto') || incident.type.includes('Trojan')) {
        return ['aws ec2 stop-instances --instance-ids i-082ecf59714651549', 'aws ec2 modify-instance-attribute --instance-id i-082ecf59714651549 --groups sg-isolated'];
      }
      if (incident.type.includes('S3') || incident.type.includes('Bucket')) {
        return ['aws s3api put-public-access-block --bucket arbitium-demo-sensitive-254886179156 --public-access-block-configuration BlockPublicAcls=true,IgnorePublicAcls=true,BlockPublicPolicy=true,RestrictPublicBuckets=true'];
      }
      if (incident.type.includes('IAM') || incident.type.includes('Credential')) {
        return ['aws iam update-access-key --access-key-id AKIA*** --status Inactive --user-name compromised-user'];
      }
    }
    if (incident.source === 'okta') {
      if (incident.title?.includes('Brute Force') || incident.type?.includes('Brute')) {
        return [
          'curl -X POST "https://trial-1462117.okta.com/api/v1/users/00uztomqveRVEXool697/lifecycle/suspend" -H "Authorization: SSWS $OKTA_TOKEN"',
          'curl -X DELETE "https://trial-1462117.okta.com/api/v1/users/00uztomqveRVEXool697/sessions" -H "Authorization: SSWS $OKTA_TOKEN"'
        ];
      }
      return ['DELETE /api/v1/users/{userId}/sessions', 'POST /api/v1/users/{userId}/lifecycle/suspend'];
    }
    if (incident.source === 'crowdstrike') {
      if (incident.title?.includes('Ransomware') || incident.title?.includes('EICAR')) {
        return [
          `curl -X PATCH "https://api.us-2.crowdstrike.com/alerts/entities/alerts/v3" -H "Authorization: Bearer $CS_TOKEN" -d '{"composite_ids":["..."],"action_parameters":[{"name":"update_status","value":"closed"}]}'`
        ];
      }
      return ['POST /devices/entities/devices-actions/v2 { "action_name": "contain", "ids": ["device_id"] }'];
    }
    return ['# Review threat details and determine appropriate response'];
  };

  const steps = [
    { id: 1, name: 'Ingestion', icon: FileText },
    { id: 2, name: 'ML Analysis', icon: Cpu },
    { id: 3, name: 'DNN Classification', icon: Brain },
    { id: 4, name: 'GenAI + RAG', icon: Zap },
    { id: 5, name: 'Ready', icon: Terminal },
    { id: 6, name: 'Executed', icon: CheckCircle },
  ];

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      critical: 'text-red-400 bg-red-400/10 border-red-400/30',
      high: 'text-orange-400 bg-orange-400/10 border-orange-400/30',
      medium: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
      low: 'text-green-400 bg-green-400/10 border-green-400/30',
    };
    return colors[severity] || colors.medium;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-xl transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-white">{incident.title}</h1>
            <span className={`px-3 py-1 text-sm font-medium rounded-xl border ${getSeverityColor(incident.severity)}`}>
              {incident.severity.toUpperCase()}
            </span>
            {executed && (
              <span className="px-3 py-1 text-sm font-medium rounded-xl border text-green-400 bg-green-400/10 border-green-400/30">
                ✓ RESOLVED
              </span>
            )}
          </div>
          <p className="text-gray-400 mt-1">{incident.description}</p>
        </div>
        <button
          onClick={() => setShowReport(true)}
          className="flex items-center gap-2 px-4 py-2 bg-arbitium-primary/20 hover:bg-arbitium-primary/30 text-arbitium-accent border border-arbitium-primary/30 rounded-xl transition-colors"
        >
          <FileText className="w-4 h-4" />
          Export Report
        </button>
      </div>

      {/* 5D Framework Analysis - Incident Specific */}
      <Framework5D 
        compact={true}
        activeThreat={{
          signalsScore: incident.severity === "critical" ? 0.95 : incident.severity === "high" ? 0.82 : incident.severity === "medium" ? 0.65 : 0.45,
          actionsCount: analysis?.pipeline?.genai?.commands?.length || 5,
          contextHits: analysis?.pipeline?.genai?.mitre?.techniques?.length || 8,
          timeVelocity: incident.severity === "critical" ? 0.92 : incident.severity === "high" ? 0.75 : 0.55,
          judgmentConfidence: analysis?.pipeline?.genai?.confidence || 0.88
        }}
      />

      {/* Pipeline Progress */}
      <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-6">AI Analysis Pipeline</h2>
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${
                  currentStep >= step.id 
                    ? currentStep === step.id && analyzing
                      ? 'bg-arbitium-primary/20 border-2 border-arbitium-primary animate-pulse shadow-lg shadow-arbitium-primary/20'
                      : 'bg-gradient-to-br from-arbitium-primary to-arbitium-accent shadow-lg shadow-arbitium-primary/20'
                    : 'bg-arbitium-darker border-2 border-arbitium-primary/20'
                }`}>
                  <step.icon className={`w-6 h-6 ${currentStep >= step.id ? 'text-white' : 'text-gray-500'}`} />
                </div>
                <span className={`text-xs mt-2 font-medium ${currentStep >= step.id ? 'text-white' : 'text-gray-500'}`}>{step.name}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-3 rounded-full ${currentStep > step.id ? 'bg-gradient-to-r from-arbitium-primary to-arbitium-accent' : 'bg-arbitium-primary/20'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Analysis Results */}
        <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Analysis Results</h2>
          
          {!analysis && !analyzing && !executed && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-arbitium-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-arbitium-primary" />
              </div>
              <p className="text-gray-400 mb-6">Run AI analysis to get remediation recommendations</p>
              <button onClick={runAnalysis} className="px-6 py-3 bg-gradient-to-r from-arbitium-primary to-arbitium-accent text-white font-medium rounded-xl flex items-center gap-2 mx-auto hover:opacity-90 transition-all shadow-lg shadow-arbitium-primary/20">
                <Play className="w-5 h-5" /> Run Live AI Analysis
              </button>
            </div>
          )}

          {analyzing && (
            <div className="flex flex-col items-center py-8">
              <Loader className="w-10 h-10 text-arbitium-primary animate-spin mb-4" />
              <p className="text-gray-400">Analyzing threat...</p>
            </div>
          )}

          {(analysis || executed) && !analyzing && (
            <div className="space-y-4">
              {analysis && (
                <>
                  <div className="bg-arbitium-darker/50 border border-arbitium-primary/10 rounded-xl p-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-400 text-sm">ML Anomaly Score</span>
                      <span className="text-white font-bold">{(analysis.pipeline.ml.anomalyScore * 100).toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-arbitium-darker rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${analysis.pipeline.ml.anomalyScore > 0.7 ? 'bg-red-500' : 'bg-yellow-500'}`} 
                        style={{ width: `${analysis.pipeline.ml.anomalyScore * 100}%` }} 
                      />
                    </div>
                  </div>

                  <div className="bg-arbitium-darker/50 border border-arbitium-primary/10 rounded-xl p-4">
                    <span className="text-gray-400 text-sm">DNN Classification</span>
                    <p className="text-white font-semibold mt-1">{analysis.pipeline.dnn.classification}</p>
                    <p className="text-sm text-gray-400">Confidence: {analysis.pipeline.dnn.confidence}%</p>
                  </div>

                  <div className="bg-arbitium-darker/50 border border-arbitium-primary/10 rounded-xl p-4">
                    <span className="text-gray-400 text-sm">MITRE ATT&CK</span>
                    <p className="text-white font-semibold mt-1">{analysis.pipeline.dnn.mitreTactic}</p>
                    <p className="text-sm text-arbitium-accent">{analysis.pipeline.dnn.mitreId}: {analysis.pipeline.dnn.mitreName}</p>
                  </div>

                  <div className="bg-arbitium-darker/50 border border-arbitium-primary/10 rounded-xl p-4">
                    <span className="text-gray-400 text-sm">AI Recommendation</span>
                    <p className="text-white mt-1">{analysis.pipeline.genai.action || 'Execute remediation'}</p>
                  </div>

                  <div className="bg-arbitium-darker/50 border border-arbitium-primary/10 rounded-xl p-4">
                    <span className="text-gray-400 text-sm">Remediation Commands</span>
                    <div className="mt-2 space-y-2">
                      {getRemediationCommands().map((cmd: string, i: number) => (
                        <div key={i} className="bg-black/50 rounded-lg p-2 font-mono text-xs text-arbitium-accent overflow-x-auto">
                          $ {cmd}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {!executed && analysis && (
                <button onClick={executeRemediation} disabled={executing} className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium rounded-xl flex items-center justify-center gap-2 hover:opacity-90 transition-all disabled:opacity-50 shadow-lg shadow-green-500/20">
                  {executing ? <><Loader className="w-5 h-5 animate-spin" /> Executing...</> : <><Zap className="w-5 h-5" /> Execute Remediation</>}
                </button>
              )}

              {/* Remediation Progress Visualization */}
              <RemediationProgress steps={remediationSteps} isActive={executing || executed} />

              {executed && !executing && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">Remediation Complete</span>
                  </div>
                  {executionResult && <p className="text-green-300 text-sm mt-1">{executionResult}</p>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Activity Log */}
        <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Activity Log</h2>
          <div className="space-y-2 max-h-[500px] overflow-y-auto font-mono text-sm">
            {activityLog.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No activity yet. Run analysis to see logs.</p>
            ) : (
              activityLog.map((log, i) => (
                <div key={i} className={`flex gap-2 ${log.type === 'success' ? 'text-green-400' : log.type === 'warning' ? 'text-yellow-400' : 'text-gray-400'}`}>
                  <span className="text-gray-600 shrink-0">[{log.time}]</span>
                  <span>{log.message}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Incident Details */}
      <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Incident Details</h2>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <span className="text-gray-400 text-sm">Source</span>
            <p className="text-white font-semibold uppercase">{incident.source}</p>
          </div>
          <div>
            <span className="text-gray-400 text-sm">Type</span>
            <p className="text-white font-semibold text-sm">{incident.type}</p>
          </div>
          <div>
            <span className="text-gray-400 text-sm">Detected</span>
            <p className="text-white font-semibold">{incident.timestamp.toLocaleString()}</p>
          </div>
          <div>
            <span className="text-gray-400 text-sm">Status</span>
            <p className={`font-semibold ${executed ? 'text-green-400' : 'text-yellow-400'}`}>
              {executed ? 'Resolved' : 'Detected'}
            </p>
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {showReport && (
        <ReportModal
          incident={incident}
          analysis={analysis}
          activityLog={activityLog}
          executionResult={executionResult}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
};

export default IncidentDetail;
